import type { ReactNode } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { formatMoney } from "@/pages/InvoiceManagement/utils/money";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { VendorRfqDetail, VendorRfqLine } from "../../types";
import { describeDelta, formatSignedMoney, type LineQuote, type RateMap, type ResponseSummary } from "../../utils/response";
import LineDescription from "./LineDescription";
import RateInput from "./RateInput";
import { askLabel, bidFormula, deltaColor, itemLabel, lastAcceptedLabel, quantityLabel, rateInputLabel, rateLabel } from "./RfqLineTable";

interface RfqLineCardsProps {
  detail: VendorRfqDetail;
  rates: RateMap;
  summary: ResponseSummary;
  onRateChange: (lineId: number, value: string) => void;
  readOnly?: boolean;
  onAskLine?: (line: VendorRfqLine) => void;
  lineExtras?: (line: VendorRfqLine) => ReactNode;
}

const label = { fontSize: "10px", color: V2_COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" } as const;
const caption = { fontSize: "11px", color: V2_COLORS.textSecondary } as const;

export default function RfqLineCards({ detail, rates, summary, onRateChange, readOnly = false, onAskLine, lineExtras }: RfqLineCardsProps) {
  const quotes = new Map(summary.lines.map((quote) => [quote.line_id, quote]));

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={label}>{`Bid per line · ${detail.lines.length}`}</Typography>
        {!readOnly && <Typography sx={caption}>pre-filled at ask price</Typography>}
      </Stack>
      {detail.lines.map((line) => {
        const quote = quotes.get(line.line_id) as LineQuote;
        const changed = !readOnly && quote.changed;
        const lastAccepted = lastAcceptedLabel(line);
        const extras = lineExtras?.(line);
        return (
          <Box key={line.line_id} data-testid="line-card" data-changed={String(changed)} sx={{ backgroundColor: changed ? V2_COLORS.primaryBg : "white", border: `1px solid ${V2_COLORS.border}`, borderRadius: "8px", p: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize="11px" fontWeight={600} sx={{ color: V2_COLORS.primary, letterSpacing: "0.5px" }}>
                  {line.room}
                </Typography>
                <Typography fontSize="14px" fontWeight={600}>
                  {line.trade}
                </Typography>
                <Typography fontSize="12px" sx={{ color: V2_COLORS.textSecondary }}>
                  {line.item_name}
                </Typography>
                <Typography sx={{ ...caption, mt: 0.5 }}>{`${itemLabel(line)} · ${quantityLabel(line)} · ${rateLabel(line.ask_rate, line.uom)}`}</Typography>
              </Box>
              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Typography fontSize="15px" fontWeight={700}>
                  {formatMoney(line.ask_amount)}
                </Typography>
                <Typography sx={label}>Ask price</Typography>
              </Box>
            </Stack>
            <Box sx={{ mt: 1.5, p: 1.25, borderRadius: "6px", backgroundColor: V2_COLORS.bgMuted }}>
              <Typography sx={{ ...label, mb: 0.5 }}>Specification & description</Typography>
              <LineDescription line={line} full />
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ ...label, mb: 0.5 }}>{`Your rate per ${line.uom}`}</Typography>
              {readOnly ? (
                <Typography fontSize="14px" fontWeight={600}>
                  {quote.rate === null ? "—" : rateLabel(quote.rate, line.uom)}
                </Typography>
              ) : (
                <RateInput label={rateInputLabel(line)} value={rates[line.line_id] ?? ""} unit={line.uom} onChange={(value) => onRateChange(line.line_id, value)} error={quote.error} fullWidth />
              )}
              {(lastAccepted || onAskLine) && (
                <Stack data-testid="rate-footnote" direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5, gap: 1 }}>
                  {lastAccepted && (
                    <Typography fontSize="11px" sx={{ color: V2_COLORS.primary }}>
                      {lastAccepted}
                    </Typography>
                  )}
                  {onAskLine && (
                    <Button variant="outlined" size="small" aria-label={askLabel(line)} onClick={() => onAskLine(line)} sx={{ textTransform: "none", flexShrink: 0, ml: "auto" }}>
                      Query
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
            {quote.rate !== null && (
              <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${V2_COLORS.border}` }}>
                <Box>
                  <Typography sx={label}>Bid amount</Typography>
                  <Typography fontSize="15px" fontWeight={700}>
                    {formatMoney(quote.amount)}
                  </Typography>
                  <Typography sx={caption}>{bidFormula(quote, line)}</Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography fontSize="14px" fontWeight={600} sx={{ color: deltaColor(quote.delta) }}>
                    {formatSignedMoney(quote.delta)}
                  </Typography>
                  <Typography sx={caption}>{describeDelta(quote.delta)}</Typography>
                </Box>
              </Stack>
            )}
            {extras && <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${V2_COLORS.border}` }}>{extras}</Box>}
          </Box>
        );
      })}
      <Stack data-testid="totals-card" direction="row" justifyContent="space-between" sx={{ backgroundColor: "white", border: `1px solid ${V2_COLORS.border}`, borderRadius: "8px", p: 1.5 }}>
        <Box>
          <Typography sx={label}>Your bid total</Typography>
          <Typography fontSize="16px" fontWeight={700} sx={{ color: summary.changed ? V2_COLORS.success : undefined }}>
            {summary.valid ? formatMoney(summary.total) : "—"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={label}>RFQ ask total</Typography>
          <Typography fontSize="16px" fontWeight={600}>
            {formatMoney(summary.askTotal)}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}
